import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import LocalePickerGrid from "./LocalePickerGrid";

/**
 * Assign-publisher dialog — opened from a campaign row's "+ Pub"
 * button. Captures:
 *   • which publisher (only role="publisher" or unset; admin/staff filtered out)
 *   • shave amount in the $0.10–$0.27 band ($0.01 default = pub-friendly)
 *   • cap behaviour (default capped at $0.27, optional pass-through)
 *   • Amazon locales (at least one required → Assign button stays disabled)
 *
 * Defaults are tuned to the most common ops case so admin can usually
 * just pick a publisher and click Assign.
 */
export default function AssignDialog({
  campaign, onClose,
  publishers, localeCatalog,
  pubId, onPubIdChange,
  shave, onShaveChange,
  passthrough, onPassthroughChange,
  locales, onToggleLocale,
  assigning, onSubmit,
}) {
  return (
    <Dialog open={Boolean(campaign)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sn-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign publisher to {campaign?.label}</DialogTitle>
          <DialogDescription>
            Pub gets exclusive access to this Amazon feed via their unique tracker URL. Shave amount can be tuned per pub later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Publisher</Label>
            <Select value={pubId} onValueChange={onPubIdChange}>
              <SelectTrigger data-testid="sn-assign-pub-select">
                <SelectValue placeholder="Select a publisher…" />
              </SelectTrigger>
              <SelectContent>
                {publishers.filter((p) => p.role === "publisher" || !p.role).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.email} {p.sub_id ? `· ${p.sub_id}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Shave in $0.10–$0.27 zone</Label>
            <Select value={shave} onValueChange={onShaveChange}>
              <SelectTrigger data-testid="sn-assign-shave-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No shave — full pass-through in this zone</SelectItem>
                <SelectItem value="0.01">$0.01 (1¢ — default, pub-friendly)</SelectItem>
                <SelectItem value="0.02">$0.02 (2¢ — more profitable)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">Applied only when feed_EPC is in $0.10–$0.27. Below $0.10 always passes through.</p>
          </div>
          <div className="flex items-start gap-2 border-t border-slate-100 pt-3">
            <Checkbox
              id="assign-passthrough"
              checked={passthrough}
              onCheckedChange={(v) => onPassthroughChange(Boolean(v))}
              data-testid="sn-assign-passthrough"
            />
            <div className="flex-1">
              <Label htmlFor="assign-passthrough" className="text-xs font-bold text-slate-700 cursor-pointer">
                Pass through above $0.27 (no cap)
              </Label>
              <p className="text-[10px] text-slate-500 mt-0.5">
                When checked, the pub gets the full feed EPC even when it exceeds $0.27.
                When unchecked, we cap pub payout at $0.27 and keep the upside.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">
              Amazon storefronts this pub can route traffic to
            </Label>
            <p className="text-[10px] text-slate-500 mb-2 mt-0.5">
              Pub sees one ready-to-copy tracker URL per enabled locale. Each URL has the right
              <code className="font-mono mx-1 bg-slate-100 px-1 rounded">url=https://amazon.XX</code>
              baked in so Shopnomix routes the click to the matching Amazon storefront. Default is amazon.com only.
            </p>
            <LocalePickerGrid
              catalog={localeCatalog}
              selected={locales}
              onToggle={onToggleLocale}
              testIdPrefix="sn-assign"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Selected: <span className="font-mono">{locales.length || 0}</span>
              {locales.length === 0 && (
                <span className="text-rose-600 ml-1">— pick at least one</span>
              )}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={assigning}>Cancel</Button>
          <Button onClick={onSubmit} disabled={assigning || !pubId || locales.length === 0} className="bg-[#002FA7] hover:bg-[#002089] text-white" data-testid="sn-assign-submit">
            {assigning ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
